import { useEffect, useRef, useState } from "react"
import { Loading, TextInput, FormLabel, Blockquote } from "@wfp/ui"
import Select, { SingleValue } from "react-select"
import Button from "components/Shared/Button/Button"
import ExcelUploader from "components/Shared/ExcelUploader/ExcelUploader"
import useCommon from "hooks/useCommon"
import { useNavigate } from "react-router-dom"
import { useSaveXlsFormMutation, useUploadXlsFormFileMutation } from "services/household/xlsFormSchemas"
import { useGetAllCategoriesQuery } from "services/household/categories"
import { useGetSubCategoriesQuery } from "services/household/subCategories"
import { XlsFormResult } from "interfaces/xlsForm"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { XlsFormStatus } from "constants/constants"
import ValidationFormSchema from "pages/XlsForm/ValidationFormSchema"
import { FieldsData } from "interfaces/mappingInterfaces"
import XlsFormFieldMapping from "components/XlsForm/XlsFormFieldMapping"
import { CommonGetOptions } from "interfaces/dataInterfaces"

export default function AddBaseXlsSchema() {
  const navigate = useNavigate()
  const validationRef = useRef<HTMLDivElement>(null)

  const { showSnackBar } = useCommon()
  const [name, setName] = useState("")
  const [currentCategory, setCurrentCategory] = useState<CommonGetOptions | null>(null)
  const [currentSubCategory, setCurrentSubCategory] = useState<CommonGetOptions | null>(null)
  const [file, setFile] = useState<File>()
  const [errorSaveXls, setErrorSaveXls] = useState<XlsFormResult | null>(null)
  const [fieldsData, setFieldsData] = useState<FieldsData>({
    questionLabelPosition: 0,
    choiceLabelPosition: 0,
  })

  const { data: categories, isFetching: loadingCategories } = useGetAllCategoriesQuery()
  const { data: subCategories, isFetching: loadingSubCategories } = useGetSubCategoriesQuery(
    { categoryId: Number(currentCategory?.value) },
    { skip: !currentCategory },
  )
  const [saveXls, { data: fileHeaders, reset }] = useUploadXlsFormFileMutation()
  const [saveXlsForm, { isLoading }] = useSaveXlsFormMutation()

  const handleCreate = async (): Promise<void> => {
    if (
      !name ||
      !currentCategory ||
      !currentSubCategory ||
      !file ||
      !(fieldsData.questionLabelPosition && fieldsData.choiceLabelPosition)
    ) {
      showSnackBar("Please fill all required fields", "warning")
      return
    }

    try {
      const response = await saveXlsForm({
        baseXlsFormSchemaId: 0,
        adm0code: 0,
        name,
        xlsFormSubCategoryId: Number(currentSubCategory?.value),
        file,
        questionLabelPosition: fieldsData.questionLabelPosition,
        choiceLabelPosition: fieldsData.choiceLabelPosition,
      }).unwrap()

      const message = "Base XLSForm schema added successfully"
      const warnings = response.warnings?.length
        ? response.warnings.map(item => `${item.message} ${item.list}`)?.join("\n")
        : ""
      showSnackBar(`${message}\n${warnings}`, "success")

      navigate(window.location.pathname.replace("/add", ""))
    } catch (err) {
      const error = err as FetchBaseQueryError

      if (error.status === XlsFormStatus.ValidationError) {
        setErrorSaveXls(error.data as XlsFormResult)
      } else showSnackBar("An error occurred while processing your request", "error")

      window.scrollTo({
        top: validationRef.current?.offsetTop ?? 0,
        left: 0,
        behavior: "smooth",
      })
    }
  }

  const handleClear = () => {
    setFile(undefined)
    reset()
    setFieldsData({ questionLabelPosition: 0, choiceLabelPosition: 0 })
    setErrorSaveXls(null)
  }

  const handleCategories = (item: SingleValue<CommonGetOptions>): void => {
    setCurrentCategory(item)
    currentSubCategory && setCurrentSubCategory(null)
  }

  const handleSubCategories = (item: SingleValue<CommonGetOptions>): void => {
    setCurrentSubCategory(item)
  }

  const handleFileUpload = async (fileUpload: File): Promise<void> => {
    try {
      const formData = new FormData()
      formData.append("file", fileUpload)
      await saveXls(formData).unwrap()
    } catch (error) {
      handleClear()
      showSnackBar("An error occurred while processing your request", "error")
    }
  }

  useEffect(() => {
    if (file) handleFileUpload(file)
  }, [file])

  return (
    <div>
      <div className="space-y-5 w-6/12">
        <TextInput
          labelText="Name"
          name="name"
          placeholder="Name of the xls form schema"
          type="text"
          onChange={e => setName(e.target.value)}
          className="border-gray-300 rounded-md text-base"
        />

        <div>
          <FormLabel htmlFor="category">Category</FormLabel>
          <Select
            inputId="category"
            isLoading={loadingCategories}
            placeholder="Select category"
            options={categories ?? []}
            getOptionLabel={option => option.name}
            getOptionValue={option => option.value}
            value={categories?.find(cat => cat?.value === currentCategory?.value) ?? null}
            onChange={item => handleCategories(item)}
          />
        </div>
        <div>
          <FormLabel htmlFor="sub-category">Type</FormLabel>
          <Select
            inputId="sub-category"
            isLoading={loadingSubCategories}
            placeholder="Select type"
            options={subCategories ?? []}
            getOptionLabel={option => option.name}
            getOptionValue={option => option.value}
            value={subCategories?.find(subCat => subCat?.value === currentSubCategory?.value) ?? null}
            onChange={item => handleSubCategories(item)}
          />
        </div>

        <ExcelUploader clearAll={!file} onSelect={setFile} />
        {fileHeaders && (
          <XlsFormFieldMapping
            surveyList={fileHeaders.surveyHeaders}
            choiceList={fileHeaders.choicesHeaders}
            values={fieldsData}
            setValues={setFieldsData}
          />
        )}

        {errorSaveXls?.resultMessage && (
          <Blockquote kind="error" withIcon style={{ padding: "2rem" }}>
            {errorSaveXls.resultMessage}
          </Blockquote>
        )}

        {fileHeaders && errorSaveXls && (
          <div ref={validationRef}>
            <ValidationFormSchema data={errorSaveXls} />
          </div>
        )}

        <Loading active={isLoading} />

        {fileHeaders && (
          <div className="d-flex">
            {!errorSaveXls && <Button action={handleCreate} label="Save" className="btn-full blue mr-1" />}
            <Button action={handleClear} label="Clear & Re-Upload" className="btn-full blue" />
          </div>
        )}
      </div>
    </div>
  )
}
